import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface Product {
  sku: string
  name: string
  group: number
  productUrl?: string
  description: string
  image?: string
  price?: { value: number; currency: string }
  fixedNetPrice?: { value: number; currency: string }
  fixedNetPriceLaunch?: { value: number; currency: string }
  excludedFromDiscounts?: boolean
  deliveryInWeeksFrom?: number
  deliveryInWeeksTo?: number
  availableQuantities: number[]
  isPos?: boolean
  weight: number
  height: number
  width: number
  length: number
}

interface PricingTier {
  id: number
  name: string
  discount: number
  revenueThreshold: number
  currency: string
}

export const useCartStore = defineStore('cart', () => {
  const quantities = ref<Record<string, number>>({})
  const allProducts = ref<Product[]>([])
  const pricingTiers = ref<PricingTier[]>([])
  const productTierPricing = ref<Record<string, Record<string, number>>>({})
  const currency = ref('EUR')
  const taxRate = ref(19)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Initialize products and quantities
  const initializeCart = (
    products: Product[], 
    initialQuantities: Record<string, number>, 
    tiers: PricingTier[], 
    tierPricing: Record<string, Record<string, number>> = {},
    cartCurrency?: string
  ) => {
    allProducts.value = products
    quantities.value = { ...initialQuantities }
    pricingTiers.value = tiers
    productTierPricing.value = tierPricing
    if (cartCurrency) {
      currency.value = cartCurrency
    }
    // Also try to get currency from the first tier if available
    if (!cartCurrency && tiers.length > 0 && tiers[0].currency) {
      currency.value = tiers[0].currency
    }
  }

  const totalQuantity = computed(() => {
    return Object.values(quantities.value).reduce((sum, qty) => sum + qty, 0)
  })

  // Calculate base price using tier 1 prices to avoid circular dependency
  const totalBasePrice = computed(() => {
    return allProducts.value.reduce((total, product) => {
      const qty = quantities.value[product.sku] || 0
      if (qty > 0) {
        // Check for fixed prices first
        if (product.excludedFromDiscounts) {
          const unitPrice = product.fixedNetPrice?.value || product.price?.value || 0
          return total + (unitPrice * qty)
        }
        if (product.fixedNetPriceLaunch) {
          return total + (product.fixedNetPriceLaunch.value * qty)
        }
        if (product.fixedNetPrice) {
          return total + (product.fixedNetPrice.value * qty)
        }
        
        // Use tier 1 price for base calculation
        const productPricing = productTierPricing.value[product.sku]
        const basePrice = productPricing?.tier1 || product.price?.value || 0
        return total + (basePrice * qty)
      }
      return total
    }, 0)
  })

  const currentPricingTier = computed(() => {
    // Use base price to determine tier, avoiding circular dependency
    const totalValue = totalBasePrice.value
    const tiers = [...pricingTiers.value].sort((a, b) => b.revenueThreshold - a.revenueThreshold)
    
    const tier = tiers.find(tier => totalValue >= tier.revenueThreshold)
    const selectedTier = tier || pricingTiers.value[0] || { id: 1 }
    
    // Debug log for tier changes
    console.log('Pricing tier calculation:', {
      totalBasePrice: totalValue,
      selectedTier: selectedTier.name,
      tierId: selectedTier.id,
      threshold: selectedTier.revenueThreshold
    })
    
    return selectedTier
  })

  const getNetDiscountedPrice = (product: Product): number => {
    if (product.excludedFromDiscounts) {
      if (product.fixedNetPrice) {
        return product.fixedNetPrice.value
      }
      return product.price?.value || 0
    }

    if (product.fixedNetPriceLaunch) {
      return product.fixedNetPriceLaunch.value
    }

    if (product.fixedNetPrice) {
      return product.fixedNetPrice.value
    }

    // Use tier-based pricing from productTierPricing
    const productPricing = productTierPricing.value[product.sku]
    if (productPricing && currentPricingTier.value?.id) {
      const tierKey = `tier${currentPricingTier.value.id}`
      const tierPrice = productPricing[tierKey]
      if (tierPrice !== undefined && tierPrice !== null && !isNaN(tierPrice)) {
        return tierPrice
      }
    }

    // Fallback to original price if no tier pricing available
    return product.price?.value || 0
  }

  const totalPrice = computed(() => {
    return allProducts.value.reduce((total, product) => {
      const qty = quantities.value[product.sku] || 0
      if (qty > 0) {
        return total + (getNetDiscountedPrice(product) * qty)
      }
      return total
    }, 0)
  })

  const totalWeight = computed(() => {
    return allProducts.value.reduce((total, product) => {
      const qty = quantities.value[product.sku] || 0
      if (qty > 0) {
        return total + ((product.weight * qty) / 1000) // Convert to kg
      }
      return total
    }, 0)
  })

  const shippingCosts = computed(() => {
    // IMPORTANT: Shipping costs must be calculated server-side via Xhosa API
    // based on actual packaging requirements. This store is used for quick
    // UI updates but cannot accurately calculate shipping without API call.
    // Real shipping costs are provided by the backend on the cart page.
    return 0
  })

  const totalWithShipping = computed(() => {
    return totalPrice.value + shippingCosts.value
  })

  const totalGross = computed(() => {
    return totalWithShipping.value * (1 + taxRate.value / 100)
  })

  const getItemsInCart = computed(() => {
    return allProducts.value.filter(product => (quantities.value[product.sku] || 0) > 0)
  })

  const updateQuantity = (sku: string, quantity: number) => {
    // Use spread operator to ensure reactivity
    quantities.value = {
      ...quantities.value,
      [sku]: Math.max(0, quantity)
    }
  }

  // Overloaded addToCart function to accept either Product object or SKU string
  const addToCart = (productOrSku: Product | string, quantity: number = 1) => {
    const sku = typeof productOrSku === 'string' ? productOrSku : productOrSku.sku
    const currentQty = quantities.value[sku] || 0
    // Use spread operator to ensure reactivity
    quantities.value = {
      ...quantities.value,
      [sku]: currentQty + quantity
    }
  }

  const removeFromCart = (sku: string) => {
    // Use spread operator to ensure reactivity
    quantities.value = {
      ...quantities.value,
      [sku]: 0
    }
  }

  const clearCart = () => {
    const clearedQuantities: Record<string, number> = {}
    for (const sku of Object.keys(quantities.value)) {
      clearedQuantities[sku] = 0
    }
    quantities.value = clearedQuantities
  }

  const getQuantity = (sku: string): number => {
    return quantities.value[sku] || 0
  }

  const hasItems = computed(() => {
    return totalQuantity.value > 0
  })

  // Aliases for components that expect different property names
  const totalItems = computed(() => {
    return totalQuantity.value
  })

  const items = computed(() => {
    return getItemsInCart.value.map(product => ({
      id: product.sku,
      sku: product.sku,
      name: product.name,
      price: getNetDiscountedPrice(product),
      quantity: quantities.value[product.sku] || 0,
      image: product.image,
      product: product
    }))
  })

  const formatPrice = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency.value
    }).format(amount)
  }

  const validateMinimumQuantities = (productGroups: Record<number, string>, minimumQuantities: Record<number, number>) => {
    const errors: string[] = []

    // Check overall minimum
    if (totalQuantity.value < 8) {
      errors.push('Mindestbestellmenge: 8 Stück')
    }

    // Check group minimums
    for (const [groupId, minQty] of Object.entries(minimumQuantities)) {
      const groupProducts = allProducts.value.filter(p => p.group === parseInt(groupId))
      const groupTotal = groupProducts.reduce((sum, product) => {
        return sum + (quantities.value[product.sku] || 0)
      }, 0)

      if (groupTotal > 0 && groupTotal < minQty) {
        const groupName = productGroups[parseInt(groupId)]
        errors.push(`Mindestbestellmenge für ${groupName}: ${minQty} Stück`)
      }
    }

    return errors
  }

  // Add a single product to the available products list
  const addProduct = (product: Product) => {
    if (!allProducts.value.some(p => p.sku === product.sku)) {
      allProducts.value = [...allProducts.value, product]
    }
  }

  // Update pricing info for a specific product
  const updateProductPricing = (sku: string, pricing: Record<string, number>) => {
    productTierPricing.value = {
      ...productTierPricing.value,
      [sku]: pricing
    }
  }

  // Sync quantities from backend without overwriting products
  // This is critical for maintaining cart state when navigating between pages
  const syncQuantitiesFromBackend = (backendQuantities: Record<string, number>) => {
    quantities.value = { ...backendQuantities }
  }

  return {
    quantities,
    allProducts,
    pricingTiers,
    productTierPricing,
    currency,
    taxRate,
    isLoading,
    error,
    totalQuantity,
    totalItems,
    items,
    currentPricingTier,
    totalPrice,
    totalWeight,
    shippingCosts,
    totalWithShipping,
    totalGross,
    getItemsInCart,
    hasItems,
    initializeCart,
    updateQuantity,
    addToCart,
    removeFromCart,
    clearCart,
    addProduct,
    updateProductPricing,
    syncQuantitiesFromBackend,
    getQuantity,
    getNetDiscountedPrice,
    formatPrice,
    validateMinimumQuantities,
  }
})